// Map to store active listeners for each channel
const channelListeners: Map<
  string,
  Set<(event: any, ...args: any[]) => void>
> = new Map();

// Track Statistics
export const ON_GET_TRACK_STATISTICS = 'track/get-statistics';
export const ON_GET_TRACK_STATISTICS_RESPONSE = 'track/get-statistics';

export const run = (channel: string, data?: any): void => {
  try {
    window.api?.send(channel, data);
  } catch (error) {
    console.error('Post message failed:', error);
  }
};

export const on = (
  channel: string,
  listener: (event: any, ...args: any[]) => void,
): void => {
  // Add to tracking map
  if (!channelListeners.has(channel)) {
    channelListeners.set(channel, new Set());
  }
  channelListeners.get(channel)!.add(listener);

  window.api?.on(channel, listener);
};

export const once = (
  channel: string,
  listener: (event: any, ...args: any[]) => void,
): void => {
  // For 'once' events, we'll create a wrapper that removes itself from our map
  const wrappedListener = (event: any, ...args: any[]) => {
    // Remove from our tracking map after execution
    const listeners = channelListeners.get(channel);
    if (listeners) {
      listeners.delete(wrappedListener);
      if (listeners.size === 0) {
        channelListeners.delete(channel);
      }
    }
    // Execute original listener
    listener(event, ...args);
  };

  // Add to tracking map
  if (!channelListeners.has(channel)) {
    channelListeners.set(channel, new Set());
  }
  channelListeners.get(channel)!.add(wrappedListener);

  window.api?.once(channel, wrappedListener);
};

// Optional: Add a function to remove a specific channel listener
export const removeListener = (
  channel: string,
  listener?: (event: any, ...args: any[]) => void,
): void => {
  const listeners = channelListeners.get(channel);
  if (!listeners) return;

  if (listener) {
    // Remove specific listener
    window.api?.removeListener(channel, listener);
    listeners.delete(listener);
    if (listeners.size === 0) {
      channelListeners.delete(channel);
    }
  } else {
    // Remove ALL listeners for this channel
    listeners.forEach((l) => {
      window.api?.removeListener(channel, l);
    });
    channelListeners.delete(channel);
  }
};

export const oncePromise = <T = any>(channel: string): Promise<T> => {
  return new Promise((resolve) => {
    once(channel, (payload: T) => resolve(payload));
  });
};
